<script setup>
import { ref, onMounted } from 'vue';
import NewsAPI from 'dashboard/api/news';
import NextButton from 'dashboard/components-next/button/Button.vue';

const news = ref(null);
const imageFailed = ref(false);

const fetchActiveNews = async () => {
  try {
    const { data } = await NewsAPI.active();
    if (!data.news) return;
    news.value = data.news;
    NewsAPI.markViewed(data.news.id).catch(() => {});
  } catch {
    news.value = null;
  }
};

const close = () => {
  news.value = null;
};

const openLink = () => {
  if (!news.value?.link_url) return;
  NewsAPI.markClicked(news.value.id).catch(() => {});
  window.open(news.value.link_url, '_blank', 'noopener,noreferrer');
};

onMounted(fetchActiveNews);
</script>

<!-- eslint-disable-next-line vue/no-root-v-if -->
<template>
  <div
    v-if="news"
    class="fixed inset-0 z-[10000] flex items-center justify-center p-4 bg-n-alpha-black1 backdrop-blur-[4px]"
    role="dialog"
    aria-modal="true"
    @click.self="close"
  >
    <div
      class="flex flex-col w-full max-w-2xl overflow-hidden shadow-xl rounded-xl bg-n-solid-2"
    >
      <button
        v-if="news.banner_url && !imageFailed"
        type="button"
        class="block w-full p-0 border-0 bg-n-solid-3"
        :class="{
          'cursor-pointer': news.link_url,
          'cursor-default': !news.link_url,
        }"
        data-testid="news-banner"
        @click="openLink"
      >
        <img
          :src="news.banner_url"
          :alt="news.title"
          class="block w-full h-auto"
          @error="imageFailed = true"
        />
      </button>
      <div class="flex flex-col gap-3 p-6">
        <h3 class="m-0 text-base font-medium text-n-slate-12">
          {{ news.title }}
        </h3>
        <p
          v-if="news.description"
          class="m-0 text-sm whitespace-pre-line text-n-slate-11"
        >
          {{ news.description }}
        </p>
        <p
          v-if="imageFailed"
          class="m-0 text-xs text-n-slate-10"
          data-testid="news-image-unavailable"
        >
          {{ $t('GENERAL_SETTINGS.NEWS_MODAL.IMAGE_UNAVAILABLE') }}
        </p>
        <div class="flex items-center justify-end gap-2 pt-2">
          <NextButton
            faded
            slate
            :label="$t('GENERAL_SETTINGS.NEWS_MODAL.CLOSE')"
            data-testid="news-close"
            @click="close"
          />
          <NextButton
            v-if="news.link_url"
            :label="$t('GENERAL_SETTINGS.NEWS_MODAL.OPEN_LINK')"
            data-testid="news-open-link"
            @click="openLink"
          />
        </div>
      </div>
    </div>
  </div>
</template>
